
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  schema?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogImage = 'https://static.readdy.ai/image/69983b9b992309f9f345f22b715baa7c/d976ad48ea6e824eb2b305836503c616.jfif',
  schema
}: SEOProps) => {
  const location = useLocation();
  const siteUrl = 'https://assaakademi.com.tr';
  const fullUrl = canonical ? `${siteUrl}${canonical}` : `${siteUrl}${location.pathname}`;

  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }
    updateMetaTag('author', 'ASSA İş Eğitimleri Akademisi');
    updateMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');

    // Canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', fullUrl);

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:url', fullUrl, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:image', ogImage, true);
    updateMetaTag('og:image:width', '1200', true);
    updateMetaTag('og:image:height', '630', true);
    updateMetaTag('og:locale', 'tr_TR', true);
    updateMetaTag('og:site_name', 'ASSA İş Eğitimleri Akademisi', true);

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', ogImage);

    // Last modified
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // Geo tags for local SEO
    updateMetaTag('geo.region', 'TR-80');
    updateMetaTag('geo.placename', 'Osmaniye, Türkiye');
    updateMetaTag('geo.position', '37.074256;36.261465');

    // Schema.org JSON-LD
    if (schema) {
      // Remove existing schema scripts first
      const existingScripts = document.querySelectorAll('script[type="application/ld+json"][data-page-schema="true"]');
      existingScripts.forEach(script => script.remove());

      const scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'application/ld+json');
      scriptTag.setAttribute('data-page-schema', 'true');
      scriptTag.textContent = JSON.stringify(schema);
      document.head.appendChild(scriptTag);
    }

    // Cleanup function
    return () => {
      const schemaScript = document.querySelector('script[type="application/ld+json"][data-page-schema="true"]');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, [title, description, keywords, fullUrl, ogType, ogImage, schema]);
};

// Schema.org generators
export const generateOrganizationSchema = () => {
  const siteUrl = 'https://assaakademi.com.tr';
  
  return {
    '@type': 'EducationalOrganization',
    '@id': `${siteUrl}/#organization`,
    name: 'ASSA İş Eğitimleri Akademisi',
    alternateName: 'ASSA Akademi',
    description: 'Osmaniye\'de Sağlık Bakanlığı onaylı ilkyardım eğitimi, IRATA LEVEL 3 yüksekte çalışma eğitimi, yangın güvenliği ve mesleki eğitim programları',
    url: siteUrl,
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/69983b9b992309f9f345f22b715baa7c/d976ad48ea6e824eb2b305836503c616.jfif',
      width: 200,
      height: 200
    },
    image: 'https://static.readdy.ai/image/69983b9b992309f9f345f22b715baa7c/d976ad48ea6e824eb2b305836503c616.jfif',
    telephone: '+908503462772',
    email: 'info@assaakademi.com.tr',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Adnan Menderes, Emniyet Cd. 28/B',
      addressLocality: 'Osmaniye',
      addressRegion: 'Osmaniye',
      postalCode: '80010',
      addressCountry: 'TR'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 37.074256,
      longitude: 36.261465
    },
    areaServed: {
      '@type': 'GeoCircle',
      geoMidpoint: {
        '@type': 'GeoCoordinates',
        latitude: 37.074256,
        longitude: 36.261465
      },
      geoRadius: '100000'
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00'
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Saturday',
        opens: '09:00',
        closes: '14:00'
      }
    ],
    sameAs: [
      'https://www.facebook.com/assaakademi',
      'https://www.instagram.com/assaakademi',
      'https://www.linkedin.com/company/assaakademi'
    ]
  };
};

export const generateCourseSchema = (courseName: string, description: string, duration: string, price?: string) => {
  const siteUrl = 'https://assaakademi.com.tr';
  
  return {
    '@type': 'Course',
    name: courseName,
    description: description,
    provider: {
      '@type': 'EducationalOrganization',
      name: 'ASSA İş Eğitimleri Akademisi',
      url: siteUrl
    },
    timeRequired: duration,
    educationalCredentialAwarded: 'Sağlık Bakanlığı Onaylı Sertifika',
    inLanguage: 'tr',
    availableLanguage: 'tr',
    courseMode: 'onsite',
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: 'onsite',
      location: {
        '@type': 'Place',
        name: 'ASSA İş Eğitimleri Akademisi',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Adnan Menderes, Emniyet Cd. 28/B',
          addressLocality: 'Osmaniye',
          addressRegion: 'Osmaniye',
          postalCode: '80010',
          addressCountry: 'TR'
        }
      }
    },
    ...(price && {
      offers: {
        '@type': 'Offer',
        price: price,
        priceCurrency: 'TRY',
        availability: 'https://schema.org/InStock'
      }
    })
  };
};

export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => {
  return {
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
};

export const generateBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => {
  const siteUrl = 'https://assaakademi.com.tr';
  
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${siteUrl}${item.url}`
    }))
  };
};

export const generateLocalBusinessSchema = () => {
  const siteUrl = 'https://assaakademi.com.tr';
  
  return {
    '@type': 'LocalBusiness',
    '@id': `${siteUrl}/#localbusiness`,
    name: 'ASSA İş Eğitimleri Akademisi',
    alternateName: 'ASSA Akademi Osmaniye',
    description: 'Osmaniye\'de Sağlık Bakanlığı onaylı ilkyardım eğitimi, IRATA LEVEL 3 yüksekte çalışma eğitimi, yangın güvenliği ve mesleki eğitim programları',
    url: siteUrl,
    telephone: '+908503462772',
    email: 'info@assaakademi.com.tr',
    priceRange: '$$',
    image: 'https://static.readdy.ai/image/69983b9b992309f9f345f22b715baa7c/d976ad48ea6e824eb2b305836503c616.jfif',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Adnan Menderes, Emniyet Cd. 28/B',
      addressLocality: 'Osmaniye',
      addressRegion: 'Osmaniye',
      postalCode: '80010',
      addressCountry: 'TR'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 37.074256,
      longitude: 36.261465
    },
    areaServed: [
      {
        '@type': 'City',
        name: 'Osmaniye'
      },
      {
        '@type': 'City',
        name: 'Adana'
      },
      {
        '@type': 'City',
        name: 'Gaziantep'
      },
      {
        '@type': 'City',
        name: 'Hatay'
      }
    ],
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00'
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Saturday',
        opens: '09:00',
        closes: '14:00'
      }
    ],
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '2500',
      bestRating: '5',
      worstRating: '1'
    }
  };
};

export const generateWebPageSchema = (pageName: string, description: string, url: string) => {
  const siteUrl = 'https://assaakademi.com.tr';
  
  return {
    '@type': 'WebPage',
    '@id': `${siteUrl}${url}/#webpage`,
    url: `${siteUrl}${url}`,
    name: pageName,
    description: description,
    isPartOf: {
      '@id': `${siteUrl}/#website`
    },
    about: {
      '@id': `${siteUrl}/#organization`
    },
    inLanguage: 'tr-TR'
  };
};

export const generateWebSiteSchema = () => {
  const siteUrl = 'https://assaakademi.com.tr';
  
  return {
    '@type': 'WebSite',
    '@id': `${siteUrl}/#website`,
    url: siteUrl,
    name: 'ASSA İş Eğitimleri Akademisi',
    description: 'Osmaniye ilkyardım eğitimi, iş güvenliği sertifikası ve mesleki eğitim programları',
    publisher: {
      '@id': `${siteUrl}/#organization`
    },
    inLanguage: 'tr-TR'
  };
};
